import type Store from "ember-data/store";
import type LgcConsultationReportFilledJournalEvent from "parog-web/models/lgc-consultation-report-filled-journal-event";
import type FetcherService from "parog-web/services/fetcher";

/**
 * Returns the saved and non-archived LGC reports of the patient, sorted by `occurredAt` DESC.
 *
 * `isNew` excludes a report whose save is in progress or failed: it has a patient
 * but does not exist in the API.
 */
export function patientLgcReports(
  reports: {
    filter(
      callback: (report: LgcConsultationReportFilledJournalEvent) => boolean
    ): LgcConsultationReportFilledJournalEvent[];
  },
  patientId: string
): LgcConsultationReportFilledJournalEvent[] {
  return reports
    .filter(
      report =>
        report.patient?.id === patientId && !report.isNew && !report.isArchived
    )
    .sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime());
}

/**
 * Fetches the LGC consultation reports of a patient (database + store).
 *
 * Returns the live array of all the reports in the store, so a list built on it updates
 * when a report is archived.
 */
export async function loadLgcReports(
  fetcher: FetcherService,
  store: Store,
  options: { filter: { patient_id: string } }
) {
  await fetcher.loadRecords(
    "lgc-consultation-report-filled-journal-event",
    options
  );

  return store.peekAll("lgc-consultation-report-filled-journal-event");
}

/**
 * Fetches the LGC consultation reports of a patient (database + store).
 *
 * Returns the saved and non-archived reports, sorted by `occurredAt` DESC.
 */
export async function retrieveLgcReports(
  fetcher: FetcherService,
  store: Store,
  options: { filter: { patient_id: string } }
) {
  const reports = await loadLgcReports(fetcher, store, options);

  return patientLgcReports(reports, options.filter.patient_id);
}
